/**
 * CorporateProfilePage — V2 orchestrator for the Corporate profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Single polling cycle (600s) for corporation details
 *   - Tab state (basic info — single tab, expandable later)
 *   - Drawer/dialog state for Edit, Add Campus, Impersonate flows
 *   - Toast notifications
 *
 * Props:
 *   id — Corporate ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './corporate-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import AddCampusDrawer from './components/AddCampusDrawer.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId, getRoleWeight,
} from '../../../../services/session.js';
import {
  fetchCorporation, fetchTimezones, fetchUserRoles,
  editCorporation, addCampus,
} from './api/corporateProfileApi.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  formatPhone,
  setRoleInfo,
  reloadSessionCorp,
} from '../../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const CorporateProfilePage = ({ id }) => {
  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const campusId = useMemo(() => getCampusId(), []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  const addRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  const corpId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);

  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // DATA STATE
  // --------------------------------------------------------------------------
  const [corpData, setCorpData] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [addCampusDrawerOpen, setAddCampusDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Impersonate
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------

  /** Fetch corporation data (runs on mount + every 600s) */
  const fetchCorpDataCycle = useCallback(async () => {
    const result = await fetchCorporation(campusId, corpId);
    if (result && result.length > 0) {
      setCorpData(result[0]);
    }
    setLoading(false);
  }, [campusId, corpId]);

  /** Fetch support data (timezones + roles) — once on mount */
  const fetchSupportData = useCallback(async () => {
    const [tzResult, rolesResult] = await Promise.all([
      fetchTimezones(campusId),
      fetchUserRoles(campusId),
    ]);
    setTimezones(tzResult || []);
    setUserRoles(rolesResult || []);
  }, [campusId]);

  useEffect(() => {
    fetchCorpDataCycle();
    fetchSupportData();
    const interval = setInterval(fetchCorpDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchCorpDataCycle, fetchSupportData]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  /** Save edited basic info */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();

    const payload = [{
      corporate_id: corpId,
      corporate_name: formData.corpName,
      corporate_guid: formData.corpGuid,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      zip: formData.zipcode,
      email: formData.email,
      phone: formData.phone,
      modified_by: session.userId,
      street_address: formData.streetAddress,
    }];

    const oldPayload = [{
      corporate_id: corpId,
      corporate_name: corpData?.corporate_name,
      corporate_guid: corpData?.corporate_guid,
      country: corpData?.country,
      state: corpData?.state,
      city: corpData?.city,
      zip: corpData?.zip || corpData?.zipcode,
      email: corpData?.email,
      phone: corpData?.phone,
      street_address: corpData?.street_address,
    }];

    const result = await editCorporation(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Corporation information saved.');
      setBasicDrawerOpen(false);
      fetchCorpDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [corpId, corpData, getSessionParams, showToast, fetchCorpDataCycle]);

  /** Add campus */
  const handleAddCampus = useCallback(async (campusData) => {
    const session = getSessionParams();
    const [success, errMsg] = await addCampus(campusData, session);

    if (success) {
      showToast('success', `${campusData.campus_name} has been added.`);
      setAddCampusDrawerOpen(false);
      fetchCorpDataCycle();
    } else {
      showToast('error', `Failed to add campus${errMsg ? ': ' + errMsg : ''}.`);
    }
  }, [getSessionParams, showToast, fetchCorpDataCycle]);

  /** Impersonate */
  const handleConfirmImpersonate = useCallback(() => {
    const dData = getSession();
    if (!dData) {
      showToast('error', 'Session not found');
      return;
    }

    const campuses = corpData?.campuses || [];
    if (campuses.length === 0) {
      showToast('error', 'No campuses to impersonate');
      return;
    }

    setImpersonating(true);
    const selectedWeight = parseInt(impersonateRoleWeight, 10);
    setRoleInfo(selectedWeight, dData);
    reloadSessionCorp(corpId, -99, dData.cognito, dData);
  }, [corpData, corpId, impersonateRoleWeight, showToast]);

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => {
    return (userRoles || [])
      .filter((r) => iAmHigherAuth(r.user_role_weight, myRoleWeight))
      .sort((a, b) => (a.user_role_weight || 0) - (b.user_role_weight || 0));
  }, [userRoles, myRoleWeight]);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const corpName = corpData?.corporate_name || '';
  const campuses = corpData?.campuses || [];

  const bannerMeta = useMemo(() => [
    { label: 'Email', value: corpData?.email || '—' },
    { label: 'Phone', value: corpData?.phone ? formatPhone(corpData.phone) : '—' },
    {
      label: 'Location',
      value: [corpData?.city, corpData?.state].filter(Boolean).join(', ') || '—',
    },
  ], [corpData]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => [
    { key: 'basic', label: 'Basic Info' },
  ], []);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/customers/corporations" className="dp-breadcrumb__text">Corporations</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{corpName || 'Corporate Profile'}</span>
              </nav>
              <div className="crpp-header-actions">
                {addRights && (
                  <button
                    type="button"
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={() => {
                      if (campuses.length === 0) {
                        showToast('error', 'No campuses to impersonate');
                        return;
                      }
                      setImpersonateOpen(true);
                      setImpersonateRoleWeight(myRoleWeight);
                      setImpersonating(false);
                    }}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Impersonate
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={corpName}
          subtitle={`Corp ID #${corpId}`}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' && (
            <BasicInfoTab
              corpData={corpData}
              editRights={editRights}
              addRights={addRights}
              onEdit={() => setBasicDrawerOpen(true)}
              onAddCampus={() => setAddCampusDrawerOpen(true)}
            />
          )}
        </div>

        {/* Basic Info Drawer (Edit Corporation) */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          corpData={corpData}
        />

        {/* Add Campus Drawer */}
        <AddCampusDrawer
          open={addCampusDrawerOpen}
          onClose={() => setAddCampusDrawerOpen(false)}
          onSave={handleAddCampus}
          corporateId={corpId}
          corporateName={corpName}
          timezones={timezones}
        />

        {/* Impersonate Dialog */}
        {impersonateOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setImpersonateOpen(false)} aria-hidden="true">
            <div className="crpp-impersonate-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Impersonate">
              <h3 className="crpp-impersonate-dialog__title">Impersonate</h3>
              <p className="crpp-impersonate-dialog__corp">
                {corpName}
                {campuses.length > 0 ? (
                  <span className="crpp-impersonate-dialog__campuses">
                    {' — '}{campuses.map((c) => c.campus_name).join(', ')}
                  </span>
                ) : null}
              </p>
              <div className="dp-drawer__field">
                <label htmlFor="crpp-impersonate-role" className="dp-drawer__label">
                  Impersonating User Role
                </label>
                <select
                  id="crpp-impersonate-role"
                  value={impersonateRoleWeight}
                  onChange={(e) => setImpersonateRoleWeight(e.target.value)}
                  className="dp-drawer__select"
                >
                  <option value={myRoleWeight}>My Role</option>
                  {filteredRoles.map((r) => (
                    <option key={r.user_role_weight} value={r.user_role_weight}>
                      {r.user_role_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="crpp-impersonate-dialog__actions">
                <button
                  type="button"
                  onClick={() => setImpersonateOpen(false)}
                  className="dp-btn dp-btn--secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmImpersonate}
                  disabled={impersonating}
                  className="dp-btn dp-btn--primary"
                >
                  {impersonating ? 'Switching...' : 'Impersonate'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

      </div>
      </div>
    </>
  );
};

export default CorporateProfilePage;
